const SIZES = {
  header: { width: 190, tagline: "text-[9px]" },
  footer: { width: 160, tagline: "text-[9px]" },
  admin: { width: 150, tagline: "text-[9px]" },
  hero: { width: 340, tagline: "text-sm" },
  intro: { width: 480, tagline: "text-base" },
};

// Letters live in an 80x100 box, stroke 16. Tesla-style geometric wordmark.
export const E_PARTS = {
  top: "0,0 80,0 80,16 0,16",
  mid: "0,42 70,42 63,58 0,58",
  bottom: "0,84 80,84 80,100 0,100",
};

const GLYPHS = {
  E: (
    <>
      <polygon points={E_PARTS.top} />
      <polygon points={E_PARTS.mid} />
      <polygon points={E_PARTS.bottom} />
    </>
  ),
  S: (
    <>
      <rect x="0" y="0" width="80" height="16" />
      <rect x="64" y="0" width="16" height="28" />
      <rect x="0" y="0" width="16" height="58" />
      <rect x="0" y="42" width="80" height="16" />
      <rect x="64" y="42" width="16" height="58" />
      <rect x="0" y="84" width="80" height="16" />
      <rect x="0" y="72" width="16" height="28" />
    </>
  ),
  R: (
    <>
      <rect x="0" y="0" width="16" height="100" />
      <rect x="0" y="0" width="80" height="16" />
      <rect x="64" y="0" width="16" height="58" />
      <rect x="0" y="42" width="80" height="16" />
      <polygon points="50,58 66,58 80,100 64,100" />
    </>
  ),
  V: <polygon points="0,0 18,0 40,78 62,0 80,0 48,100 32,100" />,
  I: <rect x="0" y="0" width="16" height="100" />,
  C: (
    <>
      <rect x="0" y="0" width="80" height="16" />
      <rect x="64" y="0" width="16" height="28" />
      <rect x="0" y="0" width="16" height="100" />
      <rect x="0" y="84" width="80" height="16" />
      <rect x="64" y="72" width="16" height="28" />
    </>
  ),
};

const GLYPH_W = { E: 80, S: 80, R: 80, V: 80, I: 16, C: 80 };
const GAP = 44;
const WORD_GAP = 110;
const TEXT = ["E", " ", "S", "E", "R", "V", "I", "C", "E"];

const layout = TEXT.reduce(
  (acc, ch) => {
    if (ch === " ") return { x: acc.x + WORD_GAP - GAP, items: acc.items };
    return { x: acc.x + GLYPH_W[ch] + GAP, items: [...acc.items, { ch, x: acc.x }] };
  },
  { x: 0, items: [] }
);
const TOTAL_W = layout.x - GAP;

// Shared with the intro animation so it can render / slide the same glyphs.
export { GLYPHS, GLYPH_W, TOTAL_W };
export const LAYOUT_ITEMS = layout.items;
export const E_WIDTH = GLYPH_W.E;

export const Wordmark = ({ width, ink }) => (
  <svg
    viewBox={`0 0 ${TOTAL_W} 100`}
    width={width}
    height={(width * 100) / TOTAL_W}
    fill={ink}
    aria-label="E Service"
    role="img"
    shapeRendering="geometricPrecision"
  >
    {layout.items.map(({ ch, x }) => (
      <g key={`${ch}-${x}`} transform={`translate(${x} 0)`}>
        {GLYPHS[ch]}
      </g>
    ))}
  </svg>
);

// Inline wordmark that flows within a line of text: inherits the current text
// color and scales to the font size, sitting on the baseline.
export const InlineWordmark = ({ className = "" }) => (
  <svg
    viewBox={`0 0 ${TOTAL_W} 100`}
    role="img"
    aria-label="E Service"
    className={className}
    style={{
      height: "0.72em",
      width: "auto",
      display: "inline-block",
      verticalAlign: "-0.02em",
      fill: "currentColor",
    }}
    shapeRendering="geometricPrecision"
  >
    {layout.items.map(({ ch, x }) => (
      <g key={`${ch}-${x}`} transform={`translate(${x} 0)`}>
        {GLYPHS[ch]}
      </g>
    ))}
  </svg>
);

// Renders a string, swapping every "E Service" mention for the inline logo.
export const BrandText = ({ children }) => {
  if (typeof children !== "string") return children;
  return children
    .split(/(E Service)/g)
    .map((part, i) => (part === "E Service" ? <InlineWordmark key={i} /> : part));
};

const Logo = ({ size = "header", showTagline = false, variant = "light", className = "", ...rest }) => {
  const cfg = SIZES[size] || SIZES.header;
  const ink = variant === "dark" ? "#0B1E3E" : "#FFFFFF";

  return (
    <div className={`inline-flex flex-col items-center ${className}`} data-testid="brand-logo" {...rest}>
      <Wordmark width={cfg.width} ink={ink} />
      {showTagline && (
        <p
          className={`${cfg.tagline} hidden sm:block mt-3 tracking-[0.14em] uppercase whitespace-nowrap`}
          style={{ fontFamily: "'Saira', sans-serif", color: variant === "dark" ? "#64748B" : "rgba(255,255,255,0.6)" }}
        >
          Service og reparation af el- og hybridbiler
        </p>
      )}
    </div>
  );
};

export default Logo;
